import { Grid, TextField } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import AutoComplete, {
  IOption,
} from "../../components/autoComplete/AutoComplete";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICollectorPlant } from "../../interfaces/ICollectorPlant";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const CollectorPlant: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();
  const { id } = useParams<{ id: string }>();
  const timeout = useRef<any>(0);

  const [name, setName] = useState("");
  const [genealogy, setGenealogy] = useState("");
  const [number, setNumber] = useState<string | number>("");
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const [plantOptions, setPlantOptions] = useState<IOption[]>([]);
  const [plantOption, setPlantOption] = useState<IOption | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const exhibitionId = queryParams.get("exhibitionId") || "";
  const collectorId = queryParams.get("collectorId") || "";

  const isViewOnly = !!id;

  const handleListPlant = useCallback(async () => {
    if (id) {
      setLoading(true);
      await api
        .get(`/collector/plants/${id}`)
        .then(response => {
          const plant: ICollectorPlant = response.data;
          setName(plant.name);
          setGenealogy(plant.genealogy || "");
          setNumber(plant.number || "");
          setPlantOption({
            id: `${plant.name} - ${plant.genealogy}`,
            value: plant.name,
            description: plant.genealogy || "",
          });
        })
        .catch(error => {
          if (error.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error.response?.data?.message || "Erro ao carregar dados da planta",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, navigate, signOut, toastr]);

  const collectorPlantToOption = (plant: ICollectorPlant): IOption => ({
    id: `${plant.name} - ${plant.genealogy}`,
    value: `${plant.name} - ${plant.genealogy}`,
    description: plant.genealogy || "",
  });

  const handleListPlantsAsync = useCallback(async (filter = "") => {
    clearInterval(timeout.current);
    timeout.current = setTimeout(async () => {
      setLoadingSearch(true);

      await api
        .get(
          `/collector/plants/dynamically-all?currentPage=0&perPage=100&orderBy=name&orderDirection=ASC&filter=${filter}`,
        )
        .then(result => {
          if (result) {
            const options = result.data.data.map(collectorPlantToOption);
            setPlantOptions(options);
          } else {
            setPlantOptions([]);
          }
        })
        .catch(() => {
          setPlantOptions([]);
        })
        .finally(() => {
          setLoadingSearch(false);
        });
    }, 500);
  }, []);

  const handleSetPlantOption = useCallback((option: IOption | null) => {
    if (option) {
      setPlantOption({
        id: option ? `${option.value} - ${option.description}` : undefined,
        value: option ? option.value.split(" - ")[0] : "",
        description: option ? option.description : "",
      });
      setName(option.value.split(" - ")[0]);
      setGenealogy(option.description);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isViewOnly) return;

    try {
      setFormErrors({});
      setLoading(true);

      const data: ICollectorPlant = {
        name,
        genealogy,
        collectorId,
        exhibitionId,
      };

      const schema = Yup.object().shape({
        name: Yup.string().required("Nome é obrigatório"),
        genealogy: Yup.string().required("Genealogia é obrigatória"),
        collectorId: Yup.string().required("ID do colecionador é obrigatório"),
        exhibitionId: Yup.string().required("ID da exposição é obrigatório"),
      });

      await schema.validate(data, { abortEarly: false });

      await api
        .post(`/collector/plants`, data)
        .then(response => {
          const plantNumber = response.data.number;
          toastr.success(
            `Planta cadastrada com sucesso! Número: ${plantNumber}`,
          );
          navigate(
            `/collector-plant/${response.data.id}?exhibitionId=${exhibitionId}&collectorId=${collectorId}`,
          );
        })
        .catch(error => {
          if (error.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error.response?.data?.message || "Erro ao cadastrar planta",
          );
        });
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(error.message || "Erro ao salvar dados");
    } finally {
      setLoading(false);
    }
  }, [
    isViewOnly,
    name,
    genealogy,
    collectorId,
    exhibitionId,
    navigate,
    signOut,
    toastr,
  ]);

  useEffect(() => {
    handleListPlant();
  }, [handleListPlant]);

  return (
    <>
      <TitleContainer>
        <h1>
          {id
            ? `Visualizar Planta ${number ? `- Nº ${number}` : ""}`
            : "Cadastrar Planta"}
        </h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            {isViewOnly && (
              <Grid item xs={12} md={4}>
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Número"
                  value={number}
                  disabled
                />
              </Grid>
            )}
            <Grid item xs={12} md={isViewOnly ? 8 : 12}>
              {isViewOnly ? (
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Nome"
                  value={name}
                  required
                  disabled={isViewOnly}
                />
              ) : (
                <AutoComplete
                  options={plantOptions}
                  setOptions={setPlantOptions}
                  option={plantOption}
                  setOption={handleSetPlantOption}
                  setInputValue={setName}
                  asyncSearch={handleListPlantsAsync}
                  loading={loadingSearch}
                  label="Nome"
                  error={!!formErrors.name}
                  errorMessage={formErrors.name}
                  required
                  autofocus={!isViewOnly}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Genealogia"
                multiline
                rows={4}
                value={genealogy}
                required
                onChange={e => setGenealogy(e.target.value)}
                error={!!formErrors.genealogy}
                helperText={formErrors.genealogy}
                disabled={isViewOnly}
              />
            </Grid>
          </Grid>

          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={() =>
                navigate(`/collector-plants/${exhibitionId}/${collectorId}`)
              }
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {!isViewOnly && (
              <ButtonTheme
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
              >
                Confirmar
              </ButtonTheme>
            )}
          </ButtonGroup>
        </Form>

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
